import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Flex } from 'antd';
import { useQuery } from 'react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRefresh } from '@fortawesome/pro-thin-svg-icons';
import { Icon } from '@fortawesome/fontawesome-svg-core';
import { Empty } from 'antd/lib';
import { getContractLinkMissingData, getVoyages } from '@client/lib/api';
import VoyageSelector from '@client/screens/fleet/VoyageDetailsScreen/components/VoyageSelector/VoyageSelector';
import LinkedContractData from '@client/components/LinkedContractData';
import { useSaveButton } from '@client/utils/hooks/useSaveButton';
import Button from '@client/components/Button';
import { useEstimate } from '../context/EstimatorContext';
import { useEstimateChangesProvider } from '../context/EstimateChangesContext';
import { useCentralStore } from '../context/CentralStoreContext';
import styles from './styles/LinkContractEstimateModal.module.css';

const LinkContractEstimateModal = () => {
	const [initialLoadDone, setInitialLoadDone] = useState(false);
	const [selectedPreviousContractId, setSelectedPreviousContractId] = useState<number | null | undefined>();

	const {
		estimate,
		onUpdateEstimate,
	} = useEstimate();

	const {
		isGroupFixed,
		cargos,
		tcTerms,
	} = useCentralStore();

	const fixingCompanyId = useMemo(() => {
		const fixingCompanyIds = new Set(tcTerms != null ?
			[tcTerms.fixingCompanyId] :
			cargos.map((c) => c.fixingCompanyId));

		const [onlyFixingCompanyId] = fixingCompanyIds;

		return fixingCompanyIds.size === 1 ? onlyFixingCompanyId ?? undefined : undefined;
	}, [cargos, tcTerms]);

	const disabled = useMemo(() => {
		return (
			isGroupFixed ||
			!estimate?.portRotationEntries ||
			estimate.portRotationEntries.length === 0
		);
	}, [isGroupFixed, estimate?.portRotationEntries]);

	const {
		onSaveChanges,
		hasPendingChanges,
		saving,
		immediateSaveChanges,
		useBlocker,
	} = useEstimateChangesProvider();

	useEffect(() => {
		if (initialLoadDone){
			return;
		}

		setSelectedPreviousContractId(estimate?.previousVoyageId);
		setInitialLoadDone(true);
	}, [estimate?.previousVoyageId]);

	const { data: allVoyages } = useQuery(
		['voyages', estimate?.vesselId, fixingCompanyId],
		() =>
			getVoyages({
				vesselId: estimate!.vesselId!,
				disablePagination: true,
				includeCompleted: true,
				fixingCompanyId,
			}),
		{
			keepPreviousData: true,
			refetchOnWindowFocus: false,
			enabled: estimate?.vesselId != null,
		},
	);

	const { data: missingData } = useQuery(
		['contractLinkMissingData', estimate?.id, selectedPreviousContractId],
		() => getContractLinkMissingData(estimate!.id, selectedPreviousContractId!),
		{
			retry: false,
			refetchOnWindowFocus: false,
			enabled: estimate?.id != null && selectedPreviousContractId != null,
		},
	);

	const hasSelectedNewContract = (
		selectedPreviousContractId != null &&
		selectedPreviousContractId !== estimate?.previousVoyageId
	);

	const saveButton = useSaveButton({
		title: estimate?.previousVoyageId && !hasPendingChanges ? 'Unlink contract' : 'Link contract',
		onSave: () => {
			if (estimate?.previousVoyageId && !hasPendingChanges) {
				immediateSaveChanges(() => {
					onUpdateEstimate({
						previousVoyageId: null,
					});
				});

				setSelectedPreviousContractId(undefined);

				return;
			}

			onSaveChanges();
		},
		hasPendingChanges:
			hasPendingChanges ||
			selectedPreviousContractId === null ||
			(!hasPendingChanges && selectedPreviousContractId != null),
		blockNavigation: false,
		loading: saving,
		visible: allVoyages && allVoyages.count > 0 && !disabled,
		pulse: hasPendingChanges,
	});

	useBlocker(
		hasPendingChanges || hasSelectedNewContract,
		'You have unsaved changes. Are you sure you want to close?',
	);

	const voyageContent = useMemo(() => {
		if (!allVoyages || allVoyages.count === 0) {
			return (
				<Empty description="There are no contracts for this vessel"/>
			);
		}

		return (
			<VoyageSelector
				disabled={disabled}
				value={selectedPreviousContractId}
				setSelectedContractId={setSelectedPreviousContractId}
				type="previous"
				onSearch={() => {}}
				voyages={allVoyages?.rows ?? []}
				startOfVesselOwnershipPeriod={null}
				onSelect={(v) => {
					setSelectedPreviousContractId(v);
					onUpdateEstimate({
						previousVoyageId: v,
					});
				}}
			/>
		);
	}, [allVoyages, selectedPreviousContractId, disabled, onUpdateEstimate]);

	if (!estimate) {
		return null;
	}

	return (
		<Flex gap={10} vertical className={styles.container}>
			{voyageContent}
			{!selectedPreviousContractId && allVoyages && allVoyages.count > 0 && (
				<Alert
					className={styles.alert}
					type='info'
					message="Select a previous contract to automatically set the following values on the estimate:"
					showIcon
					description={(
						<ul style={{ margin: 0, paddingLeft: 10 }}>
							<li>Open port</li>
							<li>Open date</li>
							<li>Vessel costs</li>
							<li>Vessel address commission</li>
							<li>Bunker quantities & prices (fetched from the completion of the previous contract)</li>
						</ul>
					)}
				/>
			)}
			{(missingData?.missing?.length ?? 0) > 0 && (
				<Alert
					className={styles.alert}
					type="warning"
					showIcon
					message="Data missing from previous contract:"
					description={(
						<ul style={{ margin: 0, paddingLeft: 10 }}>
							{missingData?.missing.map((item) => (
								<li key={item}>{item}</li>
							))}
						</ul>
					)}
				/>
			)}
			{missingData?.warnings.map((warning) => (
				<Alert
					key={warning}
					type="warning"
					showIcon
					message={warning}
				/>
			))}
			<LinkedContractData
				selectedPreviousContractId={selectedPreviousContractId}
				voyages={allVoyages?.rows ?? []}
			/>
			{saveButton}
			{estimate.previousVoyageId && !hasPendingChanges && !disabled && (
				<Button
					icon={(<FontAwesomeIcon icon={faRefresh as Icon}/>)}
					onClick={() => {
						immediateSaveChanges(() => {
							onUpdateEstimate({
								previousVoyageId: selectedPreviousContractId,
							});
						});
					}}
				>
					Refresh vessel data
				</Button>
			)}
		</Flex>
	);
};

export default LinkContractEstimateModal;
